import React from 'react';
import { useSelector } from 'react-redux';
import { ApplicationState } from 'store/store';
import {
    IAdjustmentLocalResource,
    ILocalCouponAdjustment,
    IUpdateOrderAdjustment,
    OrderAdjustmentType
} from '../model/Order';

export function usePlatformAwards(
    exiatingAwards: IAdjustmentLocalResource[] = [],
    existingCoupons: ILocalCouponAdjustment[] = []
) {
    const { settings } = useSelector((state: ApplicationState) => state.settings);
    const [platformAwardsEnabled, setPlatformAwardsEnabled] = React.useState(
        exiatingAwards.length > 1 || existingCoupons.length >= 1
    );
    const [platformAwards, setPlatformAwards] = React.useState<IUpdateOrderAdjustment[]>(
        exiatingAwards.length > 1 || existingCoupons.length >= 1
            ? exiatingAwards.map(item => ({
                  type: item.type,
                  awardId: item.awardId,
                  quantity: item.quantity,
                  selectedOn: item.selectedOn
              }))
            : []
    );
    const showMoreAwardsButton = React.useMemo(
        () => settings?.loyalty?.multipleAwardRedemptionsEnabled ?? false,
        [settings?.loyalty?.multipleAwardRedemptionsEnabled]
    );
    const onAwardAdd = React.useCallback((adjustment: IUpdateOrderAdjustment) => {
        setPlatformAwards((existingAwards: IUpdateOrderAdjustment[]) => [
            ...existingAwards,
            { ...adjustment, selectedOn: Date.now() }
        ]);
    }, []);
    const onAwardRemove = React.useCallback((adjustmentId: string) => {
        setPlatformAwards((existingAwards: IUpdateOrderAdjustment[]) =>
            existingAwards.filter(item => item.awardId !== adjustmentId)
        );
    }, []);
    const onMultipleAwardsEnable = React.useCallback(
        (adjustment?: IUpdateOrderAdjustment) => {
            if (!adjustment) {
                return setPlatformAwardsEnabled(true);
            }
            onAwardAdd(adjustment);
            setPlatformAwardsEnabled(true);
        },
        [onAwardAdd]
    );
    const onAwardsReset = React.useCallback(() => {
        setPlatformAwards([]);
        setPlatformAwardsEnabled(existingCoupons.length >= 1);
    }, [existingCoupons.length]);
    const awardsForCheckout = React.useMemo(() => {
        const adjustments: Partial<IUpdateOrderAdjustment>[] = [];
        if (platformAwards.length > 0) {
            platformAwards.forEach(award => {
                adjustments.push({
                    type: OrderAdjustmentType.DISCOUNT,
                    awardId: award.awardId,
                    quantity: award.quantity,
                    selectedOn: award.selectedOn
                });
            });
        }
        return adjustments;
    }, [platformAwards]);
    return {
        value: platformAwards,
        platformAwardsEnabled,
        onAwardsReset,
        onAwardAdd,
        onAwardRemove,
        onMultipleAwardsEnable,
        showMoreAwardsButton,
        awardsForCheckout
    };
}
